import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCookieConsent } from '../hooks/useCookieConsent';

// Bumped when analytics cookies were introduced — existing users are asked to
// accept the updated policy (see MigrationHandler).
export const PRIVACY_VERSION = '2026-08-08';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const { consent, reset } = useCookieConsent();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 text-orange-700 hover:text-orange-800 flex items-center gap-1"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Wróć
        </button>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Polityka Prywatności
        </h1>
        <p className="text-sm text-gray-500 mb-8">
          Ostatnia aktualizacja: 8 sierpnia 2026
        </p>

        <div className="prose prose-gray max-w-none space-y-6">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              1. Administrator danych
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Administratorem danych osobowych jest Daniel Kuśmider, prowadzący
              działalność pod adresem ul. Marii Konopnickiej 61/18, 30-302
              Kraków, NIP: 7922234750. Kontakt:{' '}
              <a
                href="mailto:kusmiderdaniel@gmail.com"
                className="text-orange-700 hover:text-orange-800"
              >
                kusmiderdaniel@gmail.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              2. Jakie dane zbieramy
            </h2>
            <p className="text-gray-700 leading-relaxed">
              W ramach korzystania z Aplikacji zbieramy:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Adres e-mail (do celów logowania i komunikacji)</li>
              <li>
                Dane firmowe (nazwa firmy, NIP, REGON, adres — podawane
                dobrowolnie)
              </li>
              <li>
                Dane klientów punktu skupu (wprowadzane przez użytkownika)
              </li>
              <li>Dane transakcyjne (kwity skupu, produkty, ceny)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              3. Cel przetwarzania danych
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Dane przetwarzamy w celu:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Świadczenia usług Aplikacji (art. 6 ust. 1 lit. b RODO)</li>
              <li>
                Zapewnienia bezpieczeństwa konta (art. 6 ust. 1 lit. f RODO)
              </li>
              <li>Komunikacji z użytkownikiem (art. 6 ust. 1 lit. f RODO)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              4. Przechowywanie danych
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Dane przechowywane są na serwerach Google Firebase (region:
              Europa) z zastosowaniem szyfrowania w spoczynku i podczas
              transmisji. Dane lokalne na urządzeniu użytkownika są szyfrowane
              za pomocą Web Crypto API.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Dane przechowywane są przez okres korzystania z Aplikacji. Po
              usunięciu konta dane zostają trwale usunięte w ciągu 30 dni.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              5. Prawa użytkownika
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Zgodnie z RODO, użytkownik ma prawo do:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Dostępu do swoich danych</li>
              <li>Sprostowania danych</li>
              <li>Usunięcia danych ("prawo do bycia zapomnianym")</li>
              <li>Ograniczenia przetwarzania</li>
              <li>Przenoszenia danych</li>
              <li>Wniesienia skargi do organu nadzorczego (UODO)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              W celu realizacji powyższych praw prosimy o kontakt na adres:
              <a
                href="mailto:kusmiderdaniel@gmail.com"
                className="text-orange-700 hover:text-orange-800 ml-1"
              >
                kusmiderdaniel@gmail.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              6. Pliki cookies
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Aplikacja wykorzystuje niezbędne pliki cookies do utrzymania sesji
              użytkownika i zapewnienia prawidłowego działania. Nie stosujemy
              cookies marketingowych.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              Wyłącznie za Twoją zgodą korzystamy z analitycznych plików cookies
              Google Analytics (Google Ireland Limited), aby liczyć odwiedziny i
              sprawdzać, które części Aplikacji są używane. Do Google
              przekazujemy wyłącznie adres odwiedzonej strony, tytuł strony oraz
              standardowe dane techniczne przeglądarki. Adresy stron zawierające
              identyfikatory dokumentów są anonimizowane — nie przekazujemy
              danych kwitów, klientów ani produktów. Bez Twojej zgody Google
              Analytics nie jest uruchamiany.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              Zgodę możesz w każdej chwili wycofać lub zmienić — jest to tak
              samo proste jak jej udzielenie.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={reset}
                className="rounded-lg border-2 border-orange-600 px-4 py-2 text-sm font-semibold text-orange-700 transition-colors hover:bg-orange-50"
              >
                Zmień ustawienia cookies
              </button>
              <span className="text-sm text-gray-600">
                {consent === 'granted' && 'Obecny wybór: analityka włączona'}
                {consent === 'denied' && 'Obecny wybór: analityka wyłączona'}
                {consent === null && 'Obecny wybór: brak decyzji'}
              </span>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              7. Zmiany polityki prywatności
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Zastrzegamy sobie prawo do zmiany niniejszej polityki prywatności.
              O zmianach użytkownicy zostaną poinformowani za pośrednictwem
              Aplikacji.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
